// Optional UTM projection using proj4 if available. Falls back to simple equirectangular meters.

export interface UTMPoint { easting: number; northing: number; zone: string; hemisphere: 'N' | 'S'; }

function lonToZone(lon: number): number {
  return Math.floor((lon + 180) / 6) + 1;
}

export async function latLonToUTM(lat: number, lon: number, hintZone?: string): Promise<UTMPoint> {
  // Try dynamic import at runtime without bundler resolution
  let proj4: any = null;
  try {
    const moduleName = 'proj4';
    // @vite-ignore
    proj4 = (await import(/* @vite-ignore */ moduleName)).default;
  } catch {}

  if (proj4) {
    try {
      const zoneNumber = hintZone ? parseInt(hintZone, 10) : lonToZone(lon);
      const hemisphere: 'N' | 'S' = lat >= 0 ? 'N' : 'S';
      const epsg = `+proj=utm +zone=${zoneNumber} ${hemisphere === 'S' ? '+south' : ''} +datum=WGS84 +units=m +no_defs`;
      const [easting, northing] = proj4('+proj=longlat +datum=WGS84 +no_defs', epsg, [lon, lat]);
      return { easting, northing, zone: String(zoneNumber), hemisphere };
    } catch {
      // fall through to approximation
    }
  }

  // Fallback: equirectangular approximation (meters) around lat
  const R = 6378137; // meters
  const latRad = (lat * Math.PI) / 180;
  const metersPerDegLat = (Math.PI * R) / 180;
  const metersPerDegLon = metersPerDegLat * Math.cos(latRad);
  const easting = lon * metersPerDegLon;
  const northing = lat * metersPerDegLat;
  const zoneNumber = hintZone ? parseInt(hintZone, 10) : lonToZone(lon);
  const hemisphere: 'N' | 'S' = lat >= 0 ? 'N' : 'S';
  return { easting, northing, zone: String(zoneNumber), hemisphere };
}

